// Pestaña «Promos de referido»: lista y detalle de las promos de referido de un canal. Venta la
// monta con `channel="SALE"` y crédito con `channel="CREDIT"`; crear, editar y cambiar estado pasan aquí.
import type { RetailerOption } from '@/shared/domain/retailerOption';
import { useCallback, useMemo, useState } from 'react';
import { Gift } from 'lucide-react';
import { toast } from 'sonner';
import { Button, ConfirmDialog, usePermissions } from '@/kit';
import { LifecycleConfirmDialog, LifecycleMasterDetail } from '@/shared/patterns/Lifecycle';
import type { RetailerScopeColumnProps } from '@/shared/patterns/ScopeChips';
import { useMultiRetailerCreate } from '../lib/useMultiRetailerCreate';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import type {
    ReferralChannel,
    SalePromotion,
    SalePromotionListItem,
    SalePromotionTransition,
} from '../api/promotionsContract';
import type { PromotionFormSources } from '../api/usePromotionFormSources';
import { usePromotionFormSources } from '../api/usePromotionFormSources';
import {
    useChangeSalePromotionStatus,
    useReferralPromotionsByRetailer,
    useSalePromotions,
} from '../api/usePromotions';
import { mergeReferralLists } from '../lib/referralKpis';
import { useSaveSalePromotion } from '../api/useSalePromotionWrites';
import {
    ACTION_COPY,
    saveSuccessCopy,
    statusChangeErrorDescription,
} from '../lib/promotionActionsCopy';
import { statusConfirmation } from './promotionLifecycle';
import {
    DEFAULT_LIST_SORT,
    EMPTY_LIST_FILTERS,
    filterPromotions,
    sortPromotions,
} from '../lib/promotionsList';
import {
    emptyReferralPromotionForm,
    referralFormValuesFromPromotion,
    toReferralPromotionRequest,
    type ReferralPromotionFormValues,
} from '../lib/referralPromotionForm';
import { referralSaveError } from '../lib/referralPromotionSaveError';
import { retailerScopeErrorCopy } from '../lib/retailerScopeError';
import { addDaysToDay, NEW_PROMOTION_DAYS } from '../lib/salePromotionDraft';
import { SAVE_ERROR_TITLE } from '../lib/salePromotionSaveError';
import { PROMOTIONS_PERMISSIONS } from '../permissions';
import { PromotionsServiceState } from './PromotionsServiceState';
import { ReferralPromotionEditor, type ReferralSaveOutcome } from './ReferralPromotionEditor';
import {
    EMPTY_REFERRAL_FILTERS,
    ReferralPromotionsList,
    type ReferralListFilters,
} from './ReferralPromotionsList';
import { ALL_RETAILERS } from '../constants/retailerFilter';

type Selection =
    | { kind: 'none' }
    | { kind: 'existing'; promotionId: string }
    | { kind: 'new'; draftKey: number };

interface PendingAction {
    promo: SalePromotionListItem;
    transition: SalePromotionTransition;
}

const NONE: Selection = { kind: 'none' };
const NO_SEGMENT_NAMES: ReadonlyMap<string, string> = new Map();

export interface ReferralPromotionsPanelProps {
    channel: ReferralChannel;
    /** Tiendas y segmentos que llenan «A quién le aplica». */
    sources: PromotionFormSources;
    now: Date;
    /** Día CDMX `YYYY-MM-DD`. */
    today: string;
    /** Cadena elegida en pantalla por quien tiene scope TENANT; sin ella manda la del token. */
    retailerId?: string;
    /** Las cadenas de quien abarca el silo: la lista junta las de todas y cada promo usa la suya. */
    retailers?: readonly RetailerOption[];
    retailersLoading?: boolean;
    /** La cadena del usuario acotado a una sola, para mostrarla fija en Alcance. */
    ownRetailerName?: string | null;
}

function isEditableStatus(promo: SalePromotionListItem): boolean {
    return promo.status !== 'EXPIRED' && promo.status !== 'ARCHIVED';
}

const asListItem = (promo: SalePromotion): SalePromotionListItem => ({
    ...promo,
    redemptionsLast30Days: 0,
});

export function ReferralPromotionsPanel({
    channel,
    sources,
    now,
    today,
    retailerId,
    retailers,
    retailersLoading = false,
    ownRetailerName = null,
}: ReferralPromotionsPanelProps) {
    const override = retailerId ? { retailerId } : {};
    const multi = retailers !== undefined;
    const { hasPermission } = usePermissions();
    const canList = hasPermission(PROMOTIONS_PERMISSIONS.list);
    const canCreate = hasPermission(PROMOTIONS_PERMISSIONS.create);
    const canUpdate = hasPermission(PROMOTIONS_PERMISSIONS.update);
    const canReadLog = hasPermission(PROMOTIONS_PERMISSIONS.readLog);

    const [filters, setFilters] = useState<ReferralListFilters>(EMPTY_REFERRAL_FILTERS);
    const [selection, setSelection] = useState<Selection>(NONE);
    const [dirty, setDirty] = useState(false);
    const [pendingSelection, setPendingSelection] = useState<Selection | null>(null);
    const [confirming, setConfirming] = useState<PendingAction | null>(null);
    const [saved, setSaved] = useState<SalePromotionListItem | null>(null);
    const [fetchingMore, setFetchingMore] = useState(false);

    const retailerIds = useMemo(
        () => (retailers ?? []).map((retailer) => retailer.retailerId),
        [retailers],
    );
    const retailerNames = useMemo(
        () => new Map((retailers ?? []).map((retailer) => [retailer.retailerId, retailer.name])),
        [retailers],
    );
    const nameOf = (id: string) => retailerNames.get(id) ?? id;
    const draft = useMultiRetailerCreate(nameOf);
    const newRetailerIds = draft.selectedIds;
    const single = useSalePromotions({
        archived: false,
        enabled: canList && !multi,
        kind: 'REFERRAL',
        channel,
        ...override,
    });
    const byRetailer = useReferralPromotionsByRetailer({
        channel,
        retailerIds,
        enabled: canList && multi,
    });
    const list = multi ? combinedList() : single;
    const changeStatus = useChangeSalePromotionStatus();
    const save = useSaveSalePromotion();

    const visible = useMemo(
        () =>
            sortPromotions(
                filterPromotions(
                    list.items.filter(
                        (promo) =>
                            filters.retailerId === ALL_RETAILERS ||
                            promo.retailerId === filters.retailerId,
                    ),
                    { ...EMPTY_LIST_FILTERS, search: filters.search, status: filters.status },
                    now,
                ),
                DEFAULT_LIST_SORT,
                now,
                NO_SEGMENT_NAMES,
            ),
        [list.items, filters, now],
    );

    // Al entrar se abre la primera de la lista, solo la primera vez que carga.
    const [autoOpened, setAutoOpened] = useState(false);
    const [firstVisible] = visible;
    if (!autoOpened && visible.length > 0) {
        setAutoOpened(true);
        if (selection.kind === 'none')
            setSelection({ kind: 'existing', promotionId: firstVisible.promotionId });
    }

    const selectedId = selection.kind === 'existing' ? selection.promotionId : null;
    const selected =
        selectedId === null
            ? null
            : (list.items.find((promo) => promo.promotionId === selectedId) ??
              (saved?.promotionId === selectedId ? saved : null));

    const activeRetailerId = !multi
        ? ''
        : selection.kind === 'new'
          ? newRetailerIds.length === 1
              ? newRetailerIds[0]
              : ''
          : (selected?.retailerId ?? '');
    const chainSources = usePromotionFormSources(multi, activeRetailerId || undefined);
    const editorSources = multi ? chainSources : sources;

    /** Con cadenas, cada escritura va a la de la promo; sin ellas, a la de la pantalla o del token. */
    const writeTarget = (promo: SalePromotionListItem) =>
        multi ? { retailerId: promo.retailerId } : override;

    const applySelection = useCallback(
        (next: Selection) => {
            save.reset();
            setDirty(false);
            setSelection(next);
        },
        [save],
    );

    const requestSelection = (next: Selection) => {
        if (dirty) setPendingSelection(next);
        else applySelection(next);
    };

    const startNew = () => {
        draft.reset(retailerIds.length === 1 ? [retailerIds[0]] : []);
        requestSelection({ kind: 'new', draftKey: Date.now() });
    };

    /** Una copia por cadena elegida, sin repetir las ya creadas; abre la primera al terminar. */
    const createInRetailers = async (
        values: ReferralPromotionFormValues,
    ): Promise<ReferralSaveOutcome> => {
        // Con varias cadenas la promo aplica en todas sus tiendas: las de una no existen en otra.
        const scoped = newRetailerIds.length > 1 ? { ...values, storeIds: [] } : values;
        const request = toReferralPromotionRequest(scoped);
        const result = await draft.run(
            (retailerId) => save.mutateAsync({ request, retailerId }),
            referralSaveError,
        );
        if (!result.ok) return { ok: false, field: result.field };
        const { first } = result;
        setSaved(asListItem(first));
        applySelection({ kind: 'existing', promotionId: first.promotionId });
        return { ok: true };
    };

    const onSave = async (values: ReferralPromotionFormValues): Promise<ReferralSaveOutcome> => {
        const promotionId = selection.kind === 'existing' ? selection.promotionId : undefined;
        if (multi && promotionId === undefined) return createInRetailers(values);
        try {
            const promo = await save.mutateAsync({
                promotionId,
                request: toReferralPromotionRequest(values),
                ...(selected ? writeTarget(selected) : override),
            });
            const copy = saveSuccessCopy(promo, promotionId === undefined);
            toast.success(copy.title, { description: copy.description });
            setSaved(asListItem(promo));
            if (promotionId === undefined)
                applySelection({ kind: 'existing', promotionId: promo.promotionId });
            return { ok: true };
        } catch (error) {
            const failure = referralSaveError(error);
            toast.error(SAVE_ERROR_TITLE, { description: failure.message });
            return { ok: false, field: failure.field };
        }
    };

    const send = ({ promo, transition }: PendingAction) => {
        const copy = ACTION_COPY[transition];
        changeStatus.mutate(
            { promotionId: promo.promotionId, transition, ...writeTarget(promo) },
            {
                onSuccess: () => {
                    setConfirming(null);
                    toast.success(copy.done, { description: promo.name });
                },
                onError: (error) => {
                    setConfirming(null);
                    toast.error(`No se pudo ${copy.verb} la promo`, {
                        description: statusChangeErrorDescription(error),
                    });
                },
            },
        );
    };

    const onAction = (promo: SalePromotionListItem, transition: SalePromotionTransition) => {
        if (ACTION_COPY[transition].confirm) setConfirming({ promo, transition });
        else send({ promo, transition });
    };

    const failed = list.error !== null && list.error !== undefined && (multi || !list.isSuccess);

    return (
        <>
            <LifecycleMasterDetail
                list={
                    <ReferralPromotionsList
                        items={visible}
                        loaded={list.isSuccess || failed}
                        now={now}
                        filters={filters}
                        onFiltersChange={setFilters}
                        selectedId={selectedId}
                        onSelect={(promo) => {
                            if (promo.promotionId === selectedId) return;
                            requestSelection({ kind: 'existing', promotionId: promo.promotionId });
                        }}
                        canCreate={canCreate}
                        onCreate={startNew}
                        emptyText={emptyText()}
                        footer={renderListFooter()}
                        retailerNames={multi ? retailerNames : undefined}
                    />
                }
                detail={renderDetail()}
            />
            {renderStatusConfirm()}
            <ConfirmDialog
                open={pendingSelection !== null}
                onOpenChange={(open) => {
                    if (!open) setPendingSelection(null);
                }}
                title="Cambios sin guardar"
                description="Vas a cambiar de promo y perderás los cambios en la actual."
                confirmLabel="Descartar cambios"
                destructive
                onConfirm={() => {
                    if (pendingSelection) applySelection(pendingSelection);
                    setPendingSelection(null);
                }}
            />
        </>
    );

    function ownRetailerColumn(): RetailerScopeColumnProps | undefined {
        return ownRetailerName ? { mode: 'fixed', label: ownRetailerName } : undefined;
    }

    function retailerOptions() {
        return [...(retailers ?? [])]
            .sort((a, b) => a.name.localeCompare(b.name, 'es-MX'))
            .map((retailer) => ({ value: retailer.retailerId, label: retailer.name }));
    }

    /** Las listas de todas las cadenas como una sola, con el primer error que haya. */
    function combinedList() {
        const merged = mergeReferralLists(byRetailer);
        const settled = !retailersLoading && byRetailer.every((one) => one.isSuccess || one.error);
        return {
            items: merged.items,
            isSuccess: settled,
            error: byRetailer.find((one) => one.error)?.error ?? null,
            hasNextPage: merged.hasNextPage,
            isFetchingNextPage: fetchingMore,
            fetchNextPage: async () => {
                setFetchingMore(true);
                try {
                    await Promise.all(
                        byRetailer
                            .filter((one) => one.hasNextPage)
                            .map((one) => one.fetchNextPage()),
                    );
                } finally {
                    setFetchingMore(false);
                }
            },
            refetch: () => Promise.all(byRetailer.map((one) => one.refetch())),
        };
    }

    function emptyText(): string {
        if (failed) return '';
        if (list.items.length > 0) return 'Sin promos que coincidan.';
        return 'No hay promos de referido todavía.';
    }

    function renderListFooter() {
        if (failed) {
            return (
                <div className="p-2">
                    <PromotionsServiceState
                        kind="failed"
                        detail={
                            retailerScopeErrorCopy(list.error) ??
                            (list.error instanceof Error ? list.error.message : undefined)
                        }
                        onRetry={() => {
                            void list.refetch();
                        }}
                    />
                </div>
            );
        }
        if (!list.hasNextPage) return null;
        return (
            <div className="flex flex-none justify-center border-t border-border p-2">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                        void list.fetchNextPage();
                    }}
                    isLoading={list.isFetchingNextPage}
                >
                    Cargar más
                </Button>
            </div>
        );
    }

    function renderDetail() {
        if (selection.kind === 'new') {
            return (
                <ReferralPromotionEditor
                    key={`new#${String(selection.draftKey)}`}
                    promotion={null}
                    channel={channel}
                    defaultValues={{
                        ...emptyReferralPromotionForm(channel),
                        validFrom: today,
                        validUntil: addDaysToDay(today, NEW_PROMOTION_DAYS),
                    }}
                    sources={editorSources}
                    now={now}
                    today={today}
                    editable={canCreate}
                    locked={draft.locked}
                    retailer={
                        multi
                            ? {
                                  mode: 'choose',
                                  options: retailerOptions(),
                                  selected: newRetailerIds,
                                  createdIds: draft.createdIds,
                                  loading: retailersLoading,
                                  onToggle: draft.toggle,
                                  error: draft.error,
                              }
                            : ownRetailerColumn()
                    }
                    canReadLog={canReadLog}
                    saving={save.isPending}
                    onSave={onSave}
                    onCloseNew={() => {
                        applySelection(NONE);
                    }}
                    onDirtyChange={setDirty}
                    onAction={() => undefined}
                />
            );
        }
        if (selected) {
            return (
                <ReferralPromotionEditor
                    key={`${selected.promotionId}#${String(selected.version)}`}
                    promotion={selected}
                    channel={channel}
                    defaultValues={referralFormValuesFromPromotion(selected)}
                    sources={editorSources}
                    now={now}
                    today={today}
                    editable={canUpdate && isEditableStatus(selected)}
                    retailer={
                        multi
                            ? { mode: 'fixed', label: nameOf(selected.retailerId) }
                            : ownRetailerColumn()
                    }
                    canReadLog={canReadLog}
                    saving={save.isPending}
                    onSave={onSave}
                    onCloseNew={() => undefined}
                    onDirtyChange={setDirty}
                    onAction={(transition) => {
                        onAction(selected, transition);
                    }}
                />
            );
        }
        return (
            <MasterDetailEmpty
                icon={<Gift className="size-10" aria-hidden />}
                title="Selecciona una promoción"
                sub="Elige una de la lista para verla o editarla, o crea una nueva con «Crear promoción»."
            />
        );
    }

    function renderStatusConfirm() {
        return (
            <LifecycleConfirmDialog
                confirmation={
                    confirming ? statusConfirmation(confirming.promo, confirming.transition) : null
                }
                loading={changeStatus.isPending}
                onCancel={() => {
                    setConfirming(null);
                }}
                onConfirm={() => {
                    if (confirming) send(confirming);
                }}
            />
        );
    }
}
